/**
 * Unified Color Palette System - Type Definitions
 *
 * 統一カラーパレットシステムの型定義
 * 全ての可視化コンポーネントで一貫した色使いを実現するための基盤
 */

/**
 * パレットの基本型
 */
export interface BasePalette {
  /** パレットの一意な識別子 */
  id: string;
  /** 表示名 */
  name: string;
  /** 説明（オプション） */
  description?: string;
}

/**
 * カテゴリカルパレット
 * 離散値（カテゴリ、グループ、種など）の表示に使用
 *
 * 使用例: チャート系列の色、散布図のグループ色
 */
export interface CategoricalPalette extends BasePalette {
  type: 'categorical';
  /** 色の配列（複数のカテゴリに対応） */
  colors: string[];
}

/**
 * シーケンシャルパレット
 * 連続値の単方向表示に使用（密度、温度など）
 *
 * 使用例: 密度可視化、強度を示すヒートマップ
 */
export interface SequentialPalette extends BasePalette {
  type: 'sequential';
  /** 色の配列（D3補間に使用、最小値から最大値への色の変化） */
  colors: string[];
}

/**
 * ダイバージングパレット
 * 連続値の双方向表示に使用（差分、相関など）
 *
 * 使用例: クロスタブの偏差モード、相関行列
 */
export interface DivergingPalette extends BasePalette {
  type: 'diverging';
  /** 色の配列（D3補間に使用、負→中央→正への色の変化） */
  colors: string[];
}

/**
 * パレットの型共用体
 */
export type Palette = CategoricalPalette | SequentialPalette | DivergingPalette;

/**
 * パレットタイプ
 */
export type PaletteType = 'categorical' | 'sequential' | 'diverging';

/**
 * パレットコレクション
 */
export interface PaletteCollection {
  /** カテゴリカルパレット */
  categorical: CategoricalPalette[];
  /** シーケンシャルパレット */
  sequential: SequentialPalette[];
  /** ダイバージングパレット */
  diverging: DivergingPalette[];
}

/**
 * 型ガード: カテゴリカルパレットかどうか判定
 */
export function isCategoricalPalette(palette: Palette): palette is CategoricalPalette {
  return palette.type === 'categorical';
}

/**
 * 型ガード: シーケンシャルパレットかどうか判定
 */
export function isSequentialPalette(palette: Palette): palette is SequentialPalette {
  return palette.type === 'sequential';
}

/**
 * 型ガード: ダイバージングパレットかどうか判定
 */
export function isDivergingPalette(palette: Palette): palette is DivergingPalette {
  return palette.type === 'diverging';
}
